import { createApiClient, type ApiClient } from "@positive/api-client";
import type { i18n } from "i18next";

import type { AuthService } from "./identity/authService";
import type { HostMode } from "./identity/host";

export interface AppApiClientOptions {
  i18n: i18n;
  auth: AuthService;
  mode: HostMode;
  supportSessionId: string | null;
}

/**
 * The application's API client: same-origin `/api` (Vite proxy in development, reverse proxy in production),
 * Accept-Language from the UI language, the in-memory access token (ADR-0003/D-2), 401 → one silent renewal and
 * retry, else sign-in again; `session_idle_expired`/`session_expired` sign out (identity contract §4).
 */
export function createAppApiClient({ i18n, auth, mode, supportSessionId }: AppApiClientOptions): ApiClient {
  const headers: Record<string, string> = {};
  // Development only: the SPA runs on the root host but talks to a tenant (contract §2).
  if (mode.kind === "tenant" && mode.devTenantHeader) headers["X-Positive-Tenant"] = mode.slug;
  if (supportSessionId) headers["X-Positive-Support-Session"] = supportSessionId;
  return createApiClient({
    baseUrl: "/api",
    apiVersion: "1.0",
    getLanguage: () => i18n.language,
    getAccessToken: auth.getAccessToken,
    onUnauthorized: auth.handleUnauthorized,
    getHeaders: () => headers,
  });
}
